import type { HandwritingElements } from "./handwriting-markup.js";
import type { HandwritingPad } from "./handwriting-pad.js";

/**
 * What each control in the dialog is joined to.
 *
 * Apart from `handwriting-panel.ts` so that the panel holds the pad, the index
 * and the box, and this holds the listeners.
 */

/**
 * What a wired panel has to be able to do.
 *
 * Two things, where the other three buttons act on the pad and are given it
 * outright. Undo, clear and a resize are the square's own business, and a panel
 * that had to pass each of them through would be three methods that say
 * nothing.
 */
export interface WiredPanel {
  /** Run the search and close the dialog. */
  search: () => void;

  /** Put the reader's cursor back where they left it. */
  restoreFocus: () => void;
}

/** The dialog, and the panel driving it. */
export interface PanelWiring {
  /** The page the dialog is in, whose window the resize is watched on. */
  readonly page: Document;

  /** The dialog and everything in it. */
  readonly elements: HandwritingElements;

  /** The square, which three of the controls act on. */
  readonly pad: HandwritingPad;

  /** The panel, which the other two do. */
  readonly panel: WiredPanel;
}

/**
 * Joins every control in the dialog to what it does.
 *
 * The resize is watched on the window rather than on the canvas, and it is
 * ignored while the dialog is closed. A reader who has turned their phone with
 * no pad open has nothing here to redraw.
 */
export function wirePanel(wiring: PanelWiring): void {
  const { page, elements, pad, panel } = wiring;

  elements.undo.addEventListener("click", () => {
    pad.undo();
  });
  elements.clear.addEventListener("click", () => {
    pad.clear();
  });
  elements.search.addEventListener("click", () => {
    panel.search();
  });
  elements.dialog.addEventListener("close", () => {
    panel.restoreFocus();
  });
  page.defaultView?.addEventListener("resize", () => {
    if (elements.dialog.open) {
      pad.resize();
    }
  });
}
